import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin,
  Clock,
  Compass,
  CheckCircle,
  Car,
  Calendar,
  Phone,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Waves,
  Coffee,
  Utensils,
  Sun,
  Camera,
  ChevronDown,
  Navigation,
  Info
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import BookingSearchModal from '../../components/BookingSearchModal';
import { trackBookNow, trackWhatsAppClick, trackPageView } from '../../utils/analytics';
import { buildLandingRoomCards } from '../../utils/landingRooms';
import LandingRoomCardGrid from '../../components/public/LandingRoomCardGrid';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const HotelDekatSelecta = () => {
  const navigate = useNavigate();
  const [showBookingSearch, setShowBookingSearch] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [content, setContent] = useState({});
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    document.title = "Hotel Dekat Selecta Batu Hanya 5 Menit | Spencer Green Hotel";
    trackPageView('/hotel-dekat-selecta');

    const fetchPageContent = async () => {
      try {
        const res = await axios.get(`${API_URL}/content/selecta`);
        if (Array.isArray(res.data)) {
          const map = {};
          res.data.forEach(item => {
            map[item.section] = item.content;
          });
          setContent(map);
        }
      } catch (err) {
        console.error('Failed to fetch selecta content:', err);
      }
    };

    const fetchRooms = async () => {
      try {
        const res = await axios.get(`${API_URL}/rooms`);
        if (res.data && res.data.length > 0) {
          setRooms(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch rooms:', err);
      }
    };

    fetchPageContent();
    fetchRooms();
  }, []);

  const handleBookNow = (context = 'Selecta Page') => {
    trackBookNow(`Selecta Landing - ${context}`);
    setShowBookingSearch(true);
  };

  const handleWhatsApp = (topic = 'Info Hotel Dekat Selecta') => {
    trackWhatsAppClick(`Selecta Landing - ${topic}`);
    const message = `Halo Spencer Green Hotel Batu, saya tertarik menginap untuk wisata ke Taman Rekreasi Selecta. Apakah ada promo atau ketersediaan kamar?`;
    window.open(`https://wa.me/6281130700206?text=${encodeURIComponent(message)}`, '_blank');
  };

  const heroContent = content.hero || {};
  const metricsContent = content.metrics || {};
  const ctaContent = content.cta || {};
  const roomCards = buildLandingRoomCards(rooms, content.rooms);

  const advantages = [
    {
      title: content.advantage_1?.title || "Hanya 5 Menit (2.8 KM) ke Gerbang Selecta",
      desc: content.advantage_1?.desc || "Spencer Green Hotel terletak di Jl. Raya Punten No. 86. Satu garis lurus ke gerbang Selecta tanpa rute memutar atau jalan sempit.",
      icon: <Navigation className="w-6 h-6 text-emerald-600" />
    },
    {
      title: content.advantage_2?.title || "Bebas Macet Jalur Pusat Kota Batu",
      desc: content.advantage_2?.desc || "Hindari macet panjang 45–60 menit dari pusat kota Batu. Anda bisa berangkat pagi santai dan tiba pertama saat Selecta baru buka.",
      icon: <Clock className="w-6 h-6 text-emerald-600" />
    },
    {
      title: content.advantage_3?.title || "Mandi Air Panas Hangat Pasca Berenang",
      desc: content.advantage_3?.desc || "Air di Selecta terkenal sangat dingin. Pulang ke hotel hanya 5 menit, Anda dan anak-anak bisa langsung mandi air hangat melimpah 24 jam.",
      icon: <Waves className="w-6 h-6 text-emerald-600" />
    },
    {
      title: content.advantage_4?.title || "Restoran & Cafe Siap Menyambut",
      desc: content.advantage_4?.desc || "Setelah lelah berkeliling taman bunga Selecta, nikmati makan siang nikmat di Kenari Restaurant atau santai ngopi di Rintik Rindu Rooftop.",
      icon: <Utensils className="w-6 h-6 text-emerald-600" />
    }
  ];

  const travelTips = [
    {
      time: content.tip_1?.time || "06.30 - 07.30 WIB",
      title: content.tip_1?.title || "Sarapan Pagi di Kenari Restaurant",
      desc: content.tip_1?.desc || "Isi energi keluarga dengan sarapan prasmanan nusantara bergizi di hotel sebelum berangkat."
    },
    {
      time: content.tip_2?.time || "07.45 WIB",
      title: content.tip_2?.title || "Berangkat 5 Menit ke Selecta",
      desc: content.tip_2?.desc || "Perjalanan super singkat 2.8 km dari Spencer Green Hotel. Jalan mulus dan bebas antrean macet."
    },
    {
      time: content.tip_3?.time || "08.00 - 11.30 WIB",
      title: content.tip_3?.title || "Waktu Foto Bunga & Wahana Terbaik",
      desc: content.tip_3?.desc || "Pagi hari adalah waktu emas: udara sejuk berembun, bunga bermekaran cerah, dan wahana belum padat antrean."
    },
    {
      time: content.tip_4?.time || "12.00 WIB",
      title: content.tip_4?.title || "Kembali Cepat ke Hotel untuk Relaksasi",
      desc: content.tip_4?.desc || "Kembali ke hotel dalam hitungan menit untuk istirahat, bilas air hangat, dan makan siang santai."
    }
  ];

  const selectaFacts = [
    { label: "Jarak Tempuh", val: metricsContent.distance || "2.8 km" },
    { label: "Waktu Tempuh", val: metricsContent.duration || "± 5 Menit" },
    { label: "Jam Operasional Selecta", val: metricsContent.hours || "06.00 - 17.00 WIB" },
    { label: "Akses Kendaraan", val: metricsContent.access || "Mobil, Elf, & Bus Besar" }
  ];

  const faqs = [
    {
      q: "Berapa menit waktu tempuh dari Spencer Green Hotel ke Selecta?",
      a: "Hanya sekitar 5 menit berkendara normal (jarak sekitar 2.8 km) karena Spencer Green Hotel berlokasi di Jl. Raya Punten No.86, yang merupakan jalan utama langsung menuju kawasan Selecta Bumiaji."
    },
    {
      q: "Apakah jalan menuju Selecta dari Spencer Green Hotel ramah bus pariwisata?",
      a: "Sangat ramah! Jalan Raya Punten merupakan jalan aspal lebar dua arah yang setiap hari dilalui bus pariwisata besar, elf, maupun kendaraan pribadi."
    },
    {
      q: "Apakah Spencer Green Hotel memiliki tempat parkir luas untuk kendaraan?",
      a: "Ya, kami menyediakan area parkir pribadi yang luas, aman, dan berpenjagaan 24 jam untuk kendaraan roda empat pribadi, minibus travel, maupun rombongan bus."
    },
    {
      q: "Apakah bisa check-in lebih awal atau titip barang sebelum ke Selecta?",
      a: "Bisa, tamu dapat menitipkan koper dan barang bawaan di resepsionis 24 jam kami terlebih dahulu jika ingin langsung berwisata pagi ke Selecta sebelum jam check-in resmi."
    },
    {
      q: "Apakah sarapan di hotel sudah buka sebelum pengunjung berangkat ke Selecta?",
      a: "Sarapan di Kenari Restaurant buka mulai pukul 06.00 WIB sehingga Anda dapat bersantap dengan tenang sebelum berangkat ke Selecta pada pagi hari."
    }
  ];

  return (
    <div className="bg-emerald-50/20 text-gray-800">
      {/* Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Hotel",
          "name": "Spencer Green Hotel Batu - Hotel Dekat Selecta",
          "description": "Hotel paling dekat dengan Taman Rekreasi Selecta Kota Batu Malang hanya 5 menit berkendara. Bebas macet, kolam renang, dan restoran nyaman.",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Jl. Raya Punten No.86, Bumiaji",
            "addressLocality": "Kota Batu",
            "addressRegion": "Jawa Timur",
            "postalCode": "65338",
            "addressCountry": "ID"
          },
          "telephone": "+6281130700206",
          "url": "https://spencergreenhotel.com/hotel-dekat-selecta"
        })}
      </script>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroContent.image || "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=1600"}
            alt={heroContent.imageAlt || "Hotel Dekat Selecta Batu - Spencer Green Hotel"}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/90 via-emerald-900/80 to-black/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center sm:text-left">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-4">
                <Navigation className="w-4 h-4" />
                {heroContent.badge || "Lokasi Terbaik Wisatawan Selecta Kota Batu"}
              </div>

              <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                {heroContent.title || "Hotel Paling Dekat ke Selecta Batu – Hanya 5 Menit"}
              </h1>

              <p className="text-emerald-100 text-base sm:text-lg lg:text-xl leading-relaxed mb-8">
                {heroContent.subtitle || "Tinggal selangkah dari pesona bunga abadi dan wahana rekreasi legendaris Selecta. Berlokasi prima di Jl. Raya Punten No. 86, bebas macet pusat kota, lengkap dengan kolam renang outdoor dan pemandangan pegunungan segar."}
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Button
                  onClick={() => handleBookNow('Hero Selecta')}
                  className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base px-8 py-6 rounded-full shadow-xl transition-all"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Cek Kamar Dekat Selecta
                </Button>

                <Button
                  onClick={() => handleWhatsApp('Hero Tanya Rute')}
                  variant="outline"
                  className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border-white/30 font-medium text-base px-6 py-6 rounded-full backdrop-blur-sm"
                >
                  <Phone className="w-5 h-5 mr-2 text-emerald-400" />
                  Chat Tim Reservasi (WA)
                </Button>
              </div>

              {/* Quick Metrics Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10 pt-8 border-t border-emerald-700/60 text-white">
                {selectaFacts.map((fact, idx) => (
                  <div key={idx} className="bg-emerald-900/40 backdrop-blur-sm p-3 rounded-xl border border-emerald-700/40">
                    <span className="block text-[11px] text-emerald-300 uppercase tracking-wider">{fact.label}</span>
                    <span className="text-base sm:text-lg font-bold text-white">{fact.val}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Keunggulan Lokasi Spencer Green Hotel */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              Keunggulan Kompetitif
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Mengapa Spencer Green Hotel adalah Pilihan Terbaik ke Selecta?
            </h2>
            <p className="text-gray-600 text-base sm:text-lg">
              Banyak wisatawan mengeluhkan kemacetan panjang dari arah pusat kota Batu atau jalan bawah saat menuju Selecta di pagi hari. Menginap di Spencer Green Hotel menyelesaikan masalah tersebut secara tuntas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {advantages.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-8 rounded-2xl bg-emerald-50/50 border border-emerald-100/80 hover:bg-emerald-50 hover:border-emerald-200 transition-all flex gap-5 items-start"
              >
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-1">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-display text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Distance & Route Info */}
      <section className="py-16 sm:py-20 bg-emerald-950 text-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-emerald-400 font-semibold tracking-wider text-xs uppercase bg-emerald-900 px-3 py-1 rounded-full">
                Petunjuk Rute & Akses
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 mb-6">
                Rute Mudah Tanpa Belokan Rumit
              </h2>
              <p className="text-emerald-200 text-base leading-relaxed mb-6">
                Dari pintu keluar Spencer Green Hotel di <strong>Jalan Raya Punten No. 86</strong>, Anda cukup berkendara lurus ke arah utara mengikuti jalur utama menuju Jalan Raya Selecta.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 bg-emerald-900/60 p-4 rounded-xl border border-emerald-800">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center text-sm flex-shrink-0">
                    1
                  </div>
                  <span className="text-sm sm:text-base text-emerald-100">
                    Titik Awal: <strong>Spencer Green Hotel Batu</strong> (Jl. Raya Punten No.86)
                  </span>
                </div>

                <div className="flex items-center gap-3 bg-emerald-900/60 p-4 rounded-xl border border-emerald-800">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center text-sm flex-shrink-0">
                    2
                  </div>
                  <span className="text-sm sm:text-base text-emerald-100">
                    Lurus ke Utara sejauh <strong>2.8 km (± 5 Menit berkendara)</strong>
                  </span>
                </div>

                <div className="flex items-center gap-3 bg-emerald-900/60 p-4 rounded-xl border border-emerald-800">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center text-sm flex-shrink-0">
                    3
                  </div>
                  <span className="text-sm sm:text-base text-emerald-100">
                    Tiba di <strong>Pintu Gerbang Utama Taman Rekreasi Selecta</strong>
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <a
                  href="https://www.google.com/maps/dir/Spencer+Green+Hotel+Batu/Taman+Rekreasi+Selecta"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-6 py-3.5 rounded-full text-sm transition-colors shadow-lg"
                >
                  <Compass className="w-4 h-4" />
                  Buka Rute di Google Maps
                </a>
                <Button
                  onClick={() => handleWhatsApp('Konsultasi Rute Selecta')}
                  variant="outline"
                  className="border-emerald-600 text-emerald-300 hover:bg-emerald-900 px-6 py-3.5 rounded-full text-sm"
                >
                  Tanya Tips Wisata ke Admin
                </Button>
              </div>
            </div>

            {/* Embedded Live Map */}
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-emerald-800 h-[380px] lg:h-[450px]">
              <iframe
                src="https://www.google.com/maps?q=-7.8332533,112.5288334&hl=id&z=14&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Peta Spencer Green Hotel Dekat Selecta"
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Itinerary Guide / Tips ke Selecta */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              Itinerary Rekomendasi
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Jadwal Sempurna Menikmati Wisata Selecta
            </h2>
            <p className="text-gray-600 text-base sm:text-lg">
              Tips praktis dari staf lokal Spencer Green Hotel agar liburan Anda di Selecta maksimal dan bebas lelah.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {travelTips.map((tip, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-emerald-50/40 rounded-2xl p-6 border border-emerald-100 flex flex-col justify-between"
              >
                <div>
                  <div className="inline-block bg-emerald-600 text-white font-mono text-xs px-3 py-1 rounded-full font-semibold mb-3">
                    {tip.time}
                  </div>
                  <h3 className="font-display text-lg font-bold text-gray-900 mb-2">{tip.title}</h3>
                  <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{tip.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Kamar Rekomendasi Dekat Selecta (fallback bila section CMS kosong) */}
      {roomCards.length === 0 && (
      <section className="py-16 sm:py-20 bg-emerald-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              Istirahat Nyaman
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Pilihan Kamar Nyaman Dekat Selecta
            </h2>
            <p className="text-gray-600 text-base sm:text-lg">
              Tersedia kamar Superior, Deluxe, dan Executive Suite dengan kasur empuk berstandar hotel berbintang untuk mengembalikan stamina Anda.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Superior Room */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-soft border border-gray-100 flex flex-col justify-between">
              <div>
                <img
                  src="https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800"
                  alt="Superior Room Spencer Green Hotel"
                  className="w-full h-56 object-cover"
                />
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold text-gray-900 mb-2">Superior Room</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Kamar nyaman berfasilitas lengkap dengan akses cepat ke kolam renang dan restoran. Pilihan hemat terbaik untuk pasangan & keluarga kecil.
                  </p>
                  <div className="text-xs text-gray-600 space-y-1.5 mb-4">
                    <div>✓ Air Hangat 24 Jam</div>
                    <div>✓ TV Kabel & WiFi Cepat</div>
                    <div>✓ Termasuk Sarapan Pagi</div>
                  </div>
                </div>
              </div>
              <div className="p-6 pt-0 border-t border-gray-100">
                <span className="text-xs text-gray-400">Mulai dari</span>
                <p className="text-2xl font-bold text-emerald-700 mb-3">Rp 850.000<span className="text-xs text-gray-400 font-normal"> /malam</span></p>
                <Button
                  onClick={() => handleBookNow('Superior Room Selecta')}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                >
                  Cek Ketersediaan
                </Button>
              </div>
            </div>

            {/* Deluxe Room */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-soft border border-emerald-300 relative flex flex-col justify-between">
              <div className="absolute top-3 right-3 bg-emerald-600 text-white text-xs px-3 py-1 rounded-full font-semibold shadow-md">
                Paling Favorit
              </div>
              <div>
                <img
                  src="https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800"
                  alt="Deluxe Room Mountain View"
                  className="w-full h-56 object-cover"
                />
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold text-gray-900 mb-2">Deluxe Room Mountain View</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Kamar luas dengan balkon privat menghadap perbukitan hijau Punten. Menikmati sejuknya udara pegunungan sepulang dari Selecta.
                  </p>
                  <div className="text-xs text-gray-600 space-y-1.5 mb-4">
                    <div>✓ Balkon Pribadi View Gunung</div>
                    <div>✓ Air Panas Melimpah & Bathtub</div>
                    <div>✓ Termasuk Sarapan Prasmanan</div>
                  </div>
                </div>
              </div>
              <div className="p-6 pt-0 border-t border-gray-100">
                <span className="text-xs text-gray-400">Mulai dari</span>
                <p className="text-2xl font-bold text-emerald-700 mb-3">Rp 1.200.000<span className="text-xs text-gray-400 font-normal"> /malam</span></p>
                <Button
                  onClick={() => handleBookNow('Deluxe Room Selecta')}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                >
                  Cek Ketersediaan
                </Button>
              </div>
            </div>

            {/* Executive Suite */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-soft border border-gray-100 flex flex-col justify-between">
              <div>
                <img
                  src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800"
                  alt="Executive Suite Family Room"
                  className="w-full h-56 object-cover"
                />
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold text-gray-900 mb-2">Executive Suite Room</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Kamar mewah terluas dengan ruang keluarga terpisah dan panorama spektakuler bukit Punten. Sempurna untuk rombongan keluarga besar.
                  </p>
                  <div className="text-xs text-gray-600 space-y-1.5 mb-4">
                    <div>✓ Living Room Luas & Smart TV</div>
                    <div>✓ Bathtub & Hot Water Shower</div>
                    <div>✓ Pemandangan Panorama 180 Derajat</div>
                  </div>
                </div>
              </div>
              <div className="p-6 pt-0 border-t border-gray-100">
                <span className="text-xs text-gray-400">Mulai dari</span>
                <p className="text-2xl font-bold text-emerald-700 mb-3">Rp 1.800.000<span className="text-xs text-gray-400 font-normal"> /malam</span></p>
                <Button
                  onClick={() => handleBookNow('Executive Room Selecta')}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                >
                  Cek Ketersediaan
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {roomCards.length > 0 && (
        <LandingRoomCardGrid
          title={content.rooms?.title || 'Pilihan Kamar'}
          subtitle={content.rooms?.subtitle || 'Menginap Nyaman Dekat Selecta'}
          cards={roomCards}
          onBook={handleBookNow}
        />
      )}

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              FAQ Wisatawan
            </span>
            <h2 className="font-display text-3xl font-bold text-gray-900 mt-3 mb-2">
              Pertanyaan Seputar Hotel Dekat Selecta
            </h2>
            <p className="text-gray-500 text-sm">
              Informasi lengkap seputar jarak, akses jalan, dan fasilitas untuk kunjungan Anda ke Selecta.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = activeFaq === index;
              return (
                <div
                  key={index}
                  className="border border-gray-200 rounded-2xl overflow-hidden transition-colors"
                >
                  <button
                    onClick={() => setActiveFaq(isOpen ? null : index)}
                    className="w-full text-left p-5 flex items-center justify-between font-semibold text-gray-900 hover:text-emerald-700 bg-white transition-colors"
                  >
                    <span className="text-base sm:text-lg pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180 text-emerald-600' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="p-5 pt-0 text-gray-600 text-sm sm:text-base leading-relaxed border-t border-gray-100 bg-emerald-50/20">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom Sticky Action Banner */}
      <section className="py-12 bg-emerald-900 text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h3 className="font-display text-2xl sm:text-3xl font-bold mb-3">
            {ctaContent.title || "Siap Liburan Tanpa Macet ke Selecta Batu?"}
          </h3>
          <p className="text-emerald-200 text-sm sm:text-base mb-6">
            {ctaContent.subtitle || "Pesan kamar Anda sekarang untuk mendapatkan harga terbaik dan konfirmasi ketersediaan instan."}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              onClick={() => handleBookNow('Bottom Action')}
              className="bg-white text-emerald-900 hover:bg-emerald-50 font-bold px-8 py-5 rounded-full"
            >
              Pesan Kamar Sekarang
            </Button>
            <Button
              onClick={() => handleWhatsApp('Bottom WhatsApp')}
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-8 py-5 rounded-full"
            >
              Hubungi Resepsionis via WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Booking Search Modal */}
      <BookingSearchModal
        isOpen={showBookingSearch}
        onClose={() => setShowBookingSearch(false)}
        onSearch={(data) => {
          navigate('/', {
            state: {
              doSearch: true,
              searchParams: data
            }
          });
        }}
      />
    </div>
  );
};

export default HotelDekatSelecta;
